import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import SnoozePicker from './SnoozePicker'
import type { CommitmentFollowupScope } from '../lib/commitmentActions'

type Props = {
  what?: string
  who?: string
  onCancel: () => void
  jobs?: { id: string; what: string }[]
  jobCount?: number
  onPick: (unix: number, scope: CommitmentFollowupScope, itemId?: string) => void
}

export default function CommitmentFollowupScopeDialog({ what, who, jobs, jobCount = jobs?.length, onCancel, onPick }: Props) {
  const [scope, setScope] = useState<CommitmentFollowupScope | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onCancel])

  const singleJob = jobCount === 1
  const pickedScope = singleJob ? 'item' : scope
  const itemId = singleJob ? jobs?.[0]?.id : selectedId ?? undefined

  if (pickedScope && (pickedScope === 'person' || !jobs?.length || itemId)) return (
    <SnoozePicker
      title={pickedScope === 'item' ? 'Follow up on this job' : 'Follow up on all jobs'}
      footerNote={singleJob ? 'When the tracker should bring this job back if still unresolved.' : pickedScope === 'item'
        ? 'Only this job will move. The other jobs keep their follow-up dates.'
        : 'Every outstanding job for this person will move to this date.'}
      onCancel={onCancel}
      onPick={(unix) => onPick(unix, pickedScope, itemId)}
    />
  )

  return createPortal(
    <div className="fixed inset-0 z-[1000] grid place-items-center bg-black/40 p-4 backdrop-blur-sm" onClick={onCancel}>
      <div role="dialog" aria-modal="true" aria-label="Choose follow-up scope"
        className="w-full max-w-sm rounded-2xl bg-white p-4 shadow-2xl dark:bg-stone-800"
        onClick={(event) => event.stopPropagation()} data-testid="commitment-followup-scope">
        <h3 className="mb-3 font-semibold">{scope === 'item' ? 'Choose the job' : 'Set follow-up for'}</h3>
        {scope === 'item' && jobs?.length ? (
          <div className="max-h-[60dvh] space-y-2 overflow-y-auto">
            {jobs.map((job) => (
              <button key={job.id} type="button" onClick={() => setSelectedId(job.id)}
                className="block w-full rounded-xl border border-stone-300 px-3 py-3 text-left text-sm dark:border-stone-600"
                data-testid={`commitment-followup-job-${job.id}`}>{job.what}</button>
            ))}
          </div>
        ) : <>
        <button type="button" autoFocus onClick={() => setScope('item')}
          className="mb-2 block w-full rounded-xl border border-sky-300 px-3 py-3 text-left hover:bg-sky-50 dark:border-sky-700 dark:hover:bg-sky-950"
          data-testid="commitment-followup-scope-item">
          <span className="block text-sm font-semibold">This job only</span>
          {what && <span className="mt-1 block break-words text-xs text-stone-500 dark:text-stone-400">{what}</span>}
        </button>
        <button type="button" onClick={() => setScope('person')}
          className="block w-full rounded-xl border border-stone-300 px-3 py-3 text-left hover:bg-stone-50 dark:border-stone-600 dark:hover:bg-stone-700"
          data-testid="commitment-followup-scope-person">
          <span className="block text-sm font-semibold">All {who ? `${who}'s` : "this person's"} jobs</span>
        </button>
        </>}
        <button type="button" onClick={onCancel} className="mt-3 min-h-9 rounded-lg px-3 text-sm text-stone-500 dark:text-stone-400">Cancel</button>
      </div>
    </div>, document.body,
  )
}
